import { useMemo } from "react";
import { useTheme } from "@mui/material/styles";

/**
 * The one place chart colour is decided.
 *
 * Every value here was validated with the data-viz palette checks against this
 * app's own card surfaces (#ffffff light, #1e293b dark) rather than eyeballed:
 * each mode's categorical set clears the lightness band, the chroma floor, the
 * colour-vision-deficiency separation floor and the normal-vision floor. The
 * dark column is the same eight hues re-stepped for the dark surface - not an
 * automatic lightening of the light one - so charts stay legible in both
 * themes.
 *
 * Three light slots and one dark slot sit below 3:1 contrast on their surface.
 * That is allowed only because every chart built on this module ships a legend,
 * a tooltip and an underlying data grid holding the same numbers, so colour is
 * never the only way to read a value.
 *
 * The slot ORDER is the safety mechanism, not decoration: series are assigned
 * slot 1, 2, 3... in sequence and never cycled. A chart that would need a ninth
 * series folds its tail into "Other" instead.
 */
const CATEGORICAL_LIGHT = [
  "#2a78d6", // 1 blue
  "#eb6834", // 2 orange
  "#1baf7a", // 3 aqua
  "#eda100", // 4 yellow
  "#e87ba4", // 5 magenta
  "#008300", // 6 green
  "#4a3aa7", // 7 violet
  "#e34948", // 8 red
];

const CATEGORICAL_DARK = [
  "#3987e5",
  "#d95926",
  "#199e70",
  "#c98500",
  "#d55181",
  "#008300",
  "#9085e9",
  "#e66767",
];

/**
 * One hue, light -> dark, for ordered categories (the job-status pipeline).
 * A ramp - not categorical slots - because swapping the order of those
 * categories would change what the chart means, and the reader should see that
 * order in the colour. Each mode's steps were validated for monotone lightness,
 * visible step gaps, and a step nearest the surface that still clears 2:1.
 */
const ORDINAL_LIGHT = ["#86b6ef", "#5598e7", "#2a78d6", "#1c5cab", "#104281"];
const ORDINAL_DARK = ["#cde2fb", "#9ec5f4", "#6da7ec", "#3987e5", "#256abf"];

/**
 * Reserved status colours - fixed, never themed, never reused as "series 4".
 * Always paired with a label or icon, since on the light surface `warning` and
 * `serious` sit below 3:1 by design.
 */
export const CHART_STATUS_COLORS = {
  good: "#0ca30c",
  warning: "#fab219",
  serious: "#ec835a",
  critical: "#d03b3b",
} as const;

export interface ChartPalette {
  /** Categorical slots, in assignment order. Take them with `series(n)`. */
  categorical: string[];
  /** `index`-th categorical slot; folds back into range rather than inventing a hue. */
  series: (index: number) => string;
  /** One-hue ramp for ordered categories, light -> dark in the current mode. */
  ordinal: string[];
  /** Two poles that read as opposite, for above/below-a-baseline data. */
  diverging: { positive: string; negative: string; neutral: string };
  /**
   * Emphasis steps of the two diverging poles, for singling one mark out (the
   * month being viewed, say) without breaking what the poles mean.
   */
  divergingCurrent: { positive: string; negative: string };
  status: typeof CHART_STATUS_COLORS;
  /** Chart chrome: recessive by design, one shade off the surface. */
  grid: string;
  axis: string;
  /** Axis ticks and legend text - ink, never a series colour. */
  label: string;
  /**
   * De-emphasis grey for a mark that is explicitly "everything else" (a folded
   * "Other" slice) or context behind a highlighted one. Deliberately not a
   * categorical slot: it must not read as one more named category. Clears 3:1
   * on both surfaces (3.59 light, 4.07 dark).
   */
  muted: string;
  /** The surface a mark sits on; also the colour of the gaps between marks. */
  surface: string;
  isDark: boolean;
}

/**
 * Chart colours for the active MUI theme. Re-derived only when the mode flips,
 * so switching themes repaints every chart consistently.
 */
export const useChartPalette = (): ChartPalette => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return useMemo(() => {
    const categorical = isDark ? CATEGORICAL_DARK : CATEGORICAL_LIGHT;

    return {
      categorical,
      series: (index: number) => categorical[index % categorical.length],
      ordinal: isDark ? ORDINAL_DARK : ORDINAL_LIGHT,
      diverging: {
        // Blue <-> red: warm/cool poles that read as opposite, with a neutral
        // grey midpoint so "nothing" looks like nothing.
        positive: isDark ? "#3987e5" : "#2a78d6",
        negative: isDark ? "#e66767" : "#e34948",
        neutral: isDark ? "#383835" : "#f0efec",
      },
      // Same hues as the poles above, several steps away in lightness. A
      // highlighted mark has to stay readable as profit or loss, so it keeps
      // its pole's hue and is set apart by lightness instead - which also
      // survives greyscale and every form of colour blindness, where a third
      // hue beside blue and red does not. All four clear 3:1 on their surface.
      divergingCurrent: {
        positive: isDark ? "#b7d3f6" : "#0d366b",
        negative: isDark ? "#f7b9b9" : "#8f2120",
      },
      status: CHART_STATUS_COLORS,
      grid: isDark ? "rgba(226, 232, 240, 0.12)" : "rgba(26, 32, 44, 0.10)",
      axis: isDark ? "rgba(226, 232, 240, 0.28)" : "rgba(26, 32, 44, 0.22)",
      label: theme.palette.text.secondary,
      muted: "#898781",
      surface: theme.palette.background.paper,
      isDark,
    };
    // theme.palette.* are derived from the mode, so it is the only real input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDark, theme.palette.text.secondary, theme.palette.background.paper]);
};

/**
 * Shared chart chrome: hairline grid and axis, ink-coloured ticks, and no
 * heavy borders. Spread into a chart's `sx` so every chart in the app reads as
 * one system.
 */
export const buildChartSx = (palette: ChartPalette) => ({
  "& .MuiChartsAxis-line": { stroke: palette.axis, strokeWidth: 1 },
  "& .MuiChartsAxis-tick": { stroke: palette.axis },
  "& .MuiChartsAxis-tickLabel": { fill: palette.label, fontSize: 12 },
  "& .MuiChartsAxis-label": { fill: palette.label, fontSize: 12 },
  // Solid hairlines: dashing reads as "threshold" or "projection" when it is
  // only a grid.
  "& .MuiChartsGrid-line": { stroke: palette.grid, strokeWidth: 1 },
  "& .MuiChartsLegend-series text": { fill: `${palette.label} !important`, fontSize: "12px !important" },
});
